import React, {useEffect, useRef, useState} from 'react';
import {Button, DatePicker, Modal, Popconfirm, Radio, Select, Space, Tag, Tooltip, Typography, message} from 'antd';
import {DownloadOutlined, DeleteOutlined} from '@ant-design/icons';
import ENDPOINTS from '../../constants/endpoints';
import {formatPath} from '../../constants/PathRoutes';
import api from '../../services/api';
import SearchTable from '../../components/admin/SearchTable';
import Form from '../../components/data_entry/form/Form';
import InputFormField from '../../components/data_entry/form/InputFormField';
import SelectFormField from '../../components/data_entry/form/SelectFormField';
import InputNumberFormField from '../../components/data_entry/form/InputNumberFormField';
import {BuildFieldErrorsDict, dateFormat} from '../../utils/utils';
import {REPORT_STATUS_COLOR} from '../../utils/colors';
import {REPORT_TYPES, getReportTypeConfig, getReportTypeLabel} from '../../constants/reportTypes';
import {downloadFile} from '../../utils/downloadFile';

const POLL_INTERVAL_MS = 6000;

const STATUS_OPTIONS = [
    {value: '', label: 'Todos os status'},
    {value: 'processing', label: 'Processando'},
    {value: 'done', label: 'Concluído'},
    {value: 'error', label: 'Erro'},
];

const STATUS_LABEL = {
    processing: 'Processando',
    done: 'Concluído',
    error: 'Erro',
};

const GROUP_BY_OPTIONS = [
    {value: 'day', label: 'Dia'},
    {value: 'week', label: 'Semana'},
    {value: 'month', label: 'Mês'},
];

const EXPORT_FORMAT_OPTIONS = [
    {value: 'csv', label: 'CSV'},
    {value: 'pdf', label: 'PDF'},
];

const REPORT_TYPE_OPTIONS = [{value: '', label: 'Todos os tipos'}, ...REPORT_TYPES];

const {RangePicker} = DatePicker;

export default function AdminReportsGenerated() {
    const [form] = Form.useForm();
    const tableRef = useRef();
    const pollingRef = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [reportTypeFilter, setReportTypeFilter] = useState('');

    const selectedReportType = Form.useWatch('report_type', form);
    const selectedConfig = getReportTypeConfig(selectedReportType);

    useEffect(() => {
        pollingRef.current = setInterval(async () => {
            try {
                const {data} = await api.get(ENDPOINTS.REPORTS_GENERATED_LIST, {
                    params: {status: 'processing', page_size: 50},
                });
                const processingIds = (data.results || []).map((r) => r.id);
                if (!processingIds.length) return;

                const details = await Promise.all(
                    processingIds.map((id) =>
                        api.get(formatPath(ENDPOINTS.REPORTS_GENERATED_DETAIL, {id}))
                    )
                );
                const anyFinished = details.some(({data: report}) => report.status !== 'processing');
                if (anyFinished) {
                    tableRef.current?.refetch();
                }
            } catch (_) {
                // erros de polling são silenciosos
            }
        }, POLL_INTERVAL_MS);
        return () => clearInterval(pollingRef.current);
    }, []);

    const openModal = () => {
        form.resetFields();
        setModalOpen(true);
    };

    const onFinish = async (values) => {
        setSubmitting(true);
        try {
            const config = getReportTypeConfig(values.report_type);
            const payload = {
                name: values.name,
                report_type: values.report_type,
                export_format: values.export_format,
                date_from: values.date_range?.[0]?.format('YYYY-MM-DD'),
                date_to: values.date_range?.[1]?.format('YYYY-MM-DD'),
            };
            if (config?.supportsGroupBy && values.group_by) {
                payload.group_by = values.group_by;
            }
            if (config?.supportsActiveWindowDays && values.active_window_days) {
                payload.active_window_days = values.active_window_days;
            }

            await api.post(ENDPOINTS.REPORTS_GENERATED_LIST, payload);
            message.success('Relatório solicitado. Acompanhe o status na tabela abaixo.');
            setModalOpen(false);
            tableRef.current?.refetch();
        } catch (error) {
            BuildFieldErrorsDict(error, form.setFields);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownload = async (record) => {
        setDownloadingId(record.id);
        try {
            await downloadFile({
                url: formatPath(ENDPOINTS.REPORTS_GENERATED_DOWNLOAD, {id: record.id}),
                filename: `${record.name}.${record.export_format}`,
            });
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDelete = async (record) => {
        try {
            await api.delete(formatPath(ENDPOINTS.REPORTS_GENERATED_DETAIL, {id: record.id}));
            message.success('Relatório excluído');
            tableRef.current?.refetch();
        } catch (_) {
            message.error('Erro ao excluir relatório');
        }
    };

    const columns = [
        {title: 'Nome', dataIndex: 'name', key: 'name'},
        {
            title: 'Tipo',
            dataIndex: 'report_type',
            key: 'report_type',
            render: (value) => getReportTypeLabel(value),
        },
        {
            title: 'Formato',
            dataIndex: 'export_format',
            key: 'export_format',
            render: (value) => <Tag>{value?.toUpperCase()}</Tag>,
        },
        {
            title: 'Período',
            key: 'period',
            render: (_, record) => `${dateFormat(record.date_from)} – ${dateFormat(record.date_to)}`,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => {
                const tag = <Tag color={REPORT_STATUS_COLOR[status] ?? 'default'}>{STATUS_LABEL[status] ?? status}</Tag>;
                return status === 'error' && record.error_message ? (
                    <Tooltip title={record.error_message}>{tag}</Tooltip>
                ) : tag;
            },
        },
        {title: 'Solicitado por', dataIndex: 'requested_by_name', key: 'requested_by_name', render: (v) => v || '-'},
        {
            title: 'Criado em',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value) => dateFormat(value, 'DD/MM/YYYY HH:mm'),
        },
        {
            title: 'Ações',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        size="small"
                        icon={<DownloadOutlined/>}
                        disabled={record.status !== 'done'}
                        loading={downloadingId === record.id}
                        onClick={() => handleDownload(record)}
                    >
                        Baixar
                    </Button>
                    <Popconfirm
                        title="Excluir este relatório?"
                        okText="Excluir"
                        cancelText="Cancelar"
                        onConfirm={() => handleDelete(record)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const filterParams = {};
    if (statusFilter) filterParams.status = statusFilter;
    if (reportTypeFilter) filterParams.report_type = reportTypeFilter;

    return (
        <>
            <Space style={{marginBottom: 16}} wrap>
                <Typography.Text strong>Status:</Typography.Text>
                <Select value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} style={{width: 180}}/>
                <Typography.Text strong>Tipo:</Typography.Text>
                <Select value={reportTypeFilter} onChange={setReportTypeFilter} options={REPORT_TYPE_OPTIONS} style={{width: 260}}/>
            </Space>

            <SearchTable
                key={`${statusFilter}-${reportTypeFilter}`}
                ref={tableRef}
                url={ENDPOINTS.REPORTS_GENERATED_LIST}
                params={filterParams}
                columns={columns}
                title="Relatórios Gerados"
                showSearch={false}
                buttonProps={{onClick: openModal, label: 'Novo Relatório'}}
            />

            <Modal
                title="Novo Relatório"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={form.submit}
                okText="Gerar"
                cancelText="Cancelar"
                confirmLoading={submitting}
                destroyOnClose
            >
                <Form form={form} onFinish={onFinish} initialValues={{export_format: 'csv'}}>
                    <InputFormField
                        placeholder="Ex: Receita Julho 2026"
                        form={{
                            label: 'Nome',
                            name: 'name',
                            rules: [{required: true, message: 'Informe o nome'}],
                        }}
                    />
                    <SelectFormField
                        options={REPORT_TYPES}
                        placeholder="Selecione o relatório"
                        form={{
                            label: 'Tipo de relatório',
                            name: 'report_type',
                            rules: [{required: true, message: 'Selecione o tipo de relatório'}],
                        }}
                    />
                    <Form.Item
                        label="Período"
                        name="date_range"
                        rules={[{required: true, message: 'Informe o período'}]}
                    >
                        <RangePicker format="DD/MM/YYYY" style={{width: '100%'}}/>
                    </Form.Item>
                    {selectedConfig?.supportsGroupBy && (
                        <SelectFormField
                            options={GROUP_BY_OPTIONS}
                            placeholder="Agrupar por"
                            form={{label: 'Agrupar por', name: 'group_by'}}
                        />
                    )}
                    {selectedConfig?.supportsActiveWindowDays && (
                        <InputNumberFormField
                            min={1}
                            placeholder="90"
                            form={{
                                label: 'Janela de dias considerada "ativo"',
                                name: 'active_window_days',
                            }}
                        />
                    )}
                    <Form.Item label="Formato de exportação" name="export_format">
                        <Radio.Group options={EXPORT_FORMAT_OPTIONS} optionType="button"/>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
